import type { AnalyticsWebClient } from '@atlaskit/analytics-listeners';

import { countDomElements } from './dom-element-count';
import { EditorDomRegistry } from './editor-dom-registry';
import { INSMSession } from './insm-session';
import type { ExperienceProperties, INSMOptions } from './types';
import { INPTracker } from './inp-measurers/inp';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

export class INSM {
	analyticsWebClient?: AnalyticsWebClient;
	runningSession?: INSMSession;
	options: INSMOptions;
	periodMeasurers: [INPTracker];

	/**
	 * Heavy tasks are tracked at the insm layer as heavy tasks
	 * are expected at times to be unrelated to the current
	 * page session.
	 */
	runningHeavyTasks: Set<string> = new Set();

	/**
	 * The editor is tracked at the insm layer (rather than per session) as it can
	 * outlive, or be mounted after, the session it is measured by.
	 */
	private editorDomRegistry: EditorDomRegistry = new EditorDomRegistry();

	constructor(options: INSMOptions) {
		this.periodMeasurers = [new INPTracker()];
		this.options = options;

		// If this does throw -- we do want an unhandledRejection rejection to be passed to the window
		// this is to ease debugging.
		options.getAnalyticsWebClient.then(
			(analyticsWebClient) => (this.analyticsWebClient = analyticsWebClient),
		);

		// No cleanup needs to be performed -- as this tooling is intended to run until the tab is closed
		// The use of beforeunload here is because using pagehide does not reliably result in the analytics
		// being fired by the analytics web client.
		// The use of this means we will miss events from mobile safari which does not reliably call this api
		// however mobile browsers do not call when a browser closes - so we expect limited mobile data from
		// this tooling in its current form.

		// window will not be defined in server envs - in these envs -- there can never be a session end
		if (typeof window !== 'undefined') {
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			window.addEventListener('beforeunload', () => {
				this.runningSession?.end({ stoppedBy: 'beforeunload' });
			});
		}
	}

	/**
	 * Starts a heavy task in the currently running session.
	 *
	 * This also pauses measurement.
	 *
	 * For PageLoads using the key 'PageLoad' will mean the heavy task duration
	 * is added to the insm session event as pageLoadTime.
	 */
	startHeavyTask(heavyTaskName: string): void {
		this.runningHeavyTasks.add(heavyTaskName);
		this.runningSession?.periodTracking?.startHeavyTask(heavyTaskName);
		this.runningSession?.periodTracking.pause(heavyTaskName);
		this.runningSession?.longAnimationFrameMeasurer.pause();
	}

	/**
	 * Ends a heavy task in the currently running session
	 */
	endHeavyTask(heavyTaskName: string): void {
		this.runningHeavyTasks.delete(heavyTaskName);
		this.runningSession?.periodTracking.resume(heavyTaskName);

		if (this.runningHeavyTasks.size === 0) {
			this.runningSession?.longAnimationFrameMeasurer.resume();
		}

		if (expValEquals('cc_editor_insm_outlier_events', 'cohort', 'test')) {
			if (heavyTaskName === 'PageLoad') {
				this.runningSession?.completePageLoad();
			}
		}
	}

	/**
	 * Registers the page editor's content DOM element (`editorView.dom`) with insm.
	 *
	 * insm does not locate the editor itself - the element is provided by the editor so
	 * that session events can report its DOM element count (`editorDomSize`).
	 *
	 * A session measures a page, so only the page's own editor should register. Secondary
	 * editors (inline comments, nested legacy content extensions) must not, as the
	 * reported size would then depend on which editor registered last.
	 *
	 * ```ts
	 * insm.registerEditorDom(editorView.dom);
	 * ```
	 */
	registerEditorDom(editorDom: HTMLElement): void {
		this.editorDomRegistry.register(editorDom);
	}

	/**
	 * Unregisters the editor's content DOM element. This is expected to be called when
	 * the editor is destroyed.
	 */
	unregisterEditorDom(editorDom: HTMLElement): void {
		this.editorDomRegistry.unregister(editorDom);
	}

	/**
	 * The number of DOM elements inside the registered editor, used as a proxy for
	 * document complexity in performance events.
	 *
	 * Undefined when no editor is registered, or the registered one is no longer in
	 * the document.
	 */
	getEditorDomSize(): number | undefined {
		const editorDom = this.editorDomRegistry.editorDom;

		return editorDom ? countDomElements(editorDom) : undefined;
	}

	/**
	 * Call this when starting a new experience.  This is expected to be wired to the product
	 * routing solution.
	 *
	 * It's expected this call will be paired with a `insm.session.startHeavyTask('PageLoad')` and
	 * subsequent `insm.session.endHeavyTask('PageLoad')` so that performance degradations linked
	 * to the page initialisation are excluded from the active interactivity monitoring.
	 *
	 * Using the key 'PageLoad' is special and will result in the heavy task duration being added to the
	 * insm session event as pageLoadTime.
	 *
	 *
	 * ```ts
	 * insm.start('edit-page', { initial: true, contentId: '9001' })
	 * insm.session.startHeavyTask('PageLoad')
	 * // ... heavy initialisation work
	 * insm.session.endHeavyTask('PageLoad')
	 * ```
	 */
	start(experienceKey: string, experienceProperties: ExperienceProperties): void {
		if (this.runningSession !== undefined) {
			this.runningSession.end({
				stoppedBy: 'new-experience',
				experienceKey: experienceKey,
				contentId: experienceProperties.contentId,
			});
		}
		this.lastStartedExperienceProperties = experienceProperties;
		if (this.options.experiences[experienceKey]?.enabled) {
			this.runningSession = new INSMSession(experienceKey, experienceProperties, this);
		}
	}

	private lastStartedExperienceProperties: ExperienceProperties | undefined;

	/**
	 * Call this to update the name of the running session after it's started
	 * In the case it's been started with an unregistered name, and there is not running
	 * session. This will also trigger the session being started.
	 */
	overrideExperienceKey(experienceKey: string): void {
		if (this.runningSession !== undefined) {
			// If there is a running session - we update its name
			this.runningSession.updateExperienceKey(experienceKey);
		} else {
			// otherwise - we assume the last session was not registered, and start it with the new name
			if (
				this.options.experiences[experienceKey]?.enabled &&
				this.lastStartedExperienceProperties
			) {
				this.runningSession = new INSMSession(
					experienceKey,
					this.lastStartedExperienceProperties,
					this,
				);
			}
		}
	}

	/**
	 * This prematurely halts any running experience measurement. It's expected to be used in
	 * scenarios such as when error boundaries are hit.
	 */
	stopEarly(reasonKey: string, description: string): void {
		this.runningSession?.earlyStop(reasonKey, description);
	}

	/**
	 * Gets the current running session details
	 */
	get session(): INSMSession | undefined {
		return this.runningSession;
	}
}
